/* eslint-disable max-lines-per-function */
/* eslint-disable max-lines */
import { LocalDate } from '@js-joda/core';

import { Address, CompleteAddress, Country, IsoCode, PartialAddress } from '@dreryk/address-common-api';
import { NfzDepartment } from '@dreryk/egabinet-nfz-configuration-frontend-api';
import {
   MedicalRecordNumber,
   PatientExternalIdentifier,
   PatientReferenceIdentifier,
} from '@dreryk/egabinet-patient-published-language-api';
import {
   Gender,
   GuardianBaseCommand,
   GuardianCreateCommand,
   GuardianUpdateCommand,
   Patient,
   PatientCreateCommand,
   PatientUpdateCommand,
} from '@dreryk/egabinet-patient-frontend-api';
import { FirstName, LastName, MiddleName } from '@dreryk/person-name-common-api';
import {
   NationalIdentificationNumber,
   NationalIdentificationNumberCountry,
   Passport,
} from '@dreryk/personal-external-identifier-common-api';

import { assertUnreachable, getCurrentLocale } from '@dreryk/react-common';
import { Choice } from '@dreryk/watermelon';

import {
   createIdentityDocumentFormPart,
   createIdentityDocumentFromFormPart,
} from '../identityDocument/IdentityDocumentFormSection';

import { PersonalDataFormBean, PersonalIdentifierType } from './common.types';
import { createGuardianExternalIdentifier } from './GuardianModal/GuardianDetailsModal/GuardianDetailsModal';
import { GuardianModalFormBean, StatutoryRepresentative } from './GuardianModal/GuardianModal.types';
import { genderSelectItems } from './PersonForm/Gender/GenderField.hooks';
import {
   getDefaultPatientIdentifierType,
   guardianPeselAsSelectItem,
   medicalRecordNumberAsSelectItem,
   nationalIdentificationNumberAsSelectItem,
   passportAsSelectItem,
   PatientSelectIdentifierType,
   peselAsSelectItem,
} from './PersonForm/PatientIdentifierTypeField/PatientIdentifierTypeField';

// eslint-disable-next-line max-statements
export const patientPersonalDataCommandTransformer = <T extends PatientCreateCommand | PatientUpdateCommand>(
   command: T,
   parameters: PersonalDataFormBean,
   patientMedicalRecordNumber?: MedicalRecordNumber | null
): T => {
   command.firstName = FirstName.of(parameters.firstName!);
   command.lastName = LastName.of(parameters.lastName!);
   if (parameters.middleName) {
      command.middleName = Choice.of(MiddleName.of(parameters.middleName));
   }

   const { externalIdentifier, medicalRecordNumber, gender, birthDate } = ((): {
      externalIdentifier?: PatientExternalIdentifier;
      medicalRecordNumber?: MedicalRecordNumber;
      gender: Gender | null;
      birthDate: LocalDate | null;
   } => {
      switch (parameters.personalIdentifierType.id) {
         case PersonalIdentifierType.PESEL: {
            const pesel = parameters.pesel!;
            return {
               externalIdentifier: PatientExternalIdentifier.of(pesel),
               gender: pesel.getGender(),
               birthDate: pesel.getBirthDate(),
            };
         }
         case PersonalIdentifierType.GUARDIAN_PESEL: {
            return {
               externalIdentifier: PatientExternalIdentifier.of(parameters.guardianPesel!),
               gender: parameters.gender!.value,
               birthDate: parameters.birthDate!,
            };
         }
         case PersonalIdentifierType.PASSPORT: {
            return {
               externalIdentifier: PatientExternalIdentifier.of(
                  Passport.of(parameters.passportCountry!, parameters.passport!, parameters.passportValidTo!)
               ),
               gender: parameters.gender!.value,
               birthDate: parameters.birthDate!,
            };
         }
         case PersonalIdentifierType.NATIONAL_IDENTIFICATION_NUMBER: {
            return {
               externalIdentifier: PatientExternalIdentifier.of(
                  NationalIdentificationNumber.of(
                     parameters.nationalIdentificationNumberCountry!,
                     parameters.nationalIdentificationNumber!
                  )
               ),
               gender: parameters.gender!.value,
               birthDate: parameters.birthDate!,
            };
         }
         case PersonalIdentifierType.MEDICAL_RECORD_NUMBER: {
            return {
               medicalRecordNumber: MedicalRecordNumber.of(parameters.medicalRecordNumber!),
               gender: parameters.gender?.value ?? null,
               birthDate: parameters.birthDate ?? null,
            };
         }
         default:
            return assertUnreachable(parameters.personalIdentifierType.id);
      }
   })();

   command.referenceIdentifier = PatientReferenceIdentifier.of(
      externalIdentifier,
      medicalRecordNumber ?? patientMedicalRecordNumber ?? undefined
   );
   command.gender = Choice.withUseDefaultIfNull(gender);
   command.birthDate = Choice.withUseDefaultIfNull(birthDate);

   command.identityDocument = Choice.withUseDefaultIfNull(
      createIdentityDocumentFromFormPart(parameters.identityDocument) ?? null
   );

   let address: PartialAddress | CompleteAddress = PartialAddress.builder()
      .withBuilding(parameters.house)
      .withCountry(parameters.country)
      .withPostcode(parameters.postcode)
      .withStreet(parameters.street)
      .withFlat(parameters.flat)
      .withTown(parameters.town)
      .build();

   // TODO-26898 Spotkanie nt. przekazywania adresu do backendu
   try {
      address = CompleteAddress.fromPartial(address);
      // eslint-disable-next-line no-empty
   } catch (e) {}

   if (command.class === 'egabinet-patient-frontend-api:PatientUpdateCommand') {
      command.address = address;
   } else {
      command.address = Choice.of<Address>(address);
   }

   command.phoneNumber = Choice.withUseDefaultIfNull(parameters.phoneNumber);
   command.emailAddress = Choice.withUseDefaultIfNull(parameters.emailAddress);
   command.nfzDepartment = Choice.withUseDefaultIfNull(parameters.nfzDepartment?.id || null);

   return command;
};

// eslint-disable-next-line max-statements
export const guardianBaseCommandDataCommandTransformer = (
   command: GuardianCreateCommand | GuardianUpdateCommand | GuardianBaseCommand,
   formData: GuardianModalFormBean
): GuardianBaseCommand => {
   const guardian = formData.guardian!;

   command.firstName = FirstName.of(guardian.firstName!);
   command.lastName = LastName.of(guardian.lastName!);
   command.statutoryRepresentative = formData.statutoryRepresentative === StatutoryRepresentative.YES;
   command.relation = formData.relationCode;

   command.guardianExternalIdentifier = createGuardianExternalIdentifier(guardian);

   command.address = Choice.of<Address>(
      CompleteAddress.builder()
         .withCountry(guardian.country)
         .withPostcode(guardian.postcode!)
         .withTown(guardian.town!)
         .withStreet(guardian.street)
         .withBuilding(guardian.house!)
         .withFlat(guardian.flat)
         .build()
   );

   command.phoneNumber = Choice.withUseDefaultIfNull(guardian.phoneNumber);
   command.emailAddress = Choice.withUseDefaultIfNull(guardian.emailAddress);

   return command;
};

const selectPatientSelectIdentifierType = (patient?: Patient): PatientSelectIdentifierType => {
   if (!patient?.referenceIdentifier) {
      return getDefaultPatientIdentifierType();
   }

   const externalIdentifier = patient.referenceIdentifier.findExternalIdentifier();
   if (externalIdentifier) {
      return externalIdentifier.accept<PatientSelectIdentifierType>({
         visitPesel: () => peselAsSelectItem,
         visitGuardianPesel: () => guardianPeselAsSelectItem,
         visitPassport: () => passportAsSelectItem,
         visitNationalIdentificationNumber: () => nationalIdentificationNumberAsSelectItem,
      });
   }

   const medicalRecordNumber = patient.referenceIdentifier.findMedicalRecordNumber();
   if (medicalRecordNumber) {
      return medicalRecordNumberAsSelectItem;
   }

   return peselAsSelectItem;
};

export const selectInitialPersonalData = (patient?: Patient): PersonalDataFormBean => {
   const patientExternalIdentifier = patient?.referenceIdentifier?.findExternalIdentifier();
   const medicalRecordNumberIdentifier = patient?.referenceIdentifier?.findMedicalRecordNumber();

   return {
      firstName: patient?.firstName?.asString() ?? null,
      middleName: patient?.middleName?.asString() ?? null,
      lastName: patient?.lastName?.asString() ?? null,
      country: patient?.address?.getCountry() ?? Country.DEFAULT,
      emailAddress: patient?.emailAddress ?? null,
      flat: patient?.address?.findFlat()?.asString() ?? null,
      house: patient?.address?.findBuilding()?.asString() ?? null,
      phoneNumber: patient?.phoneNumber ?? null,
      postcode: patient?.address?.findPostcode() ?? null,
      street: patient?.address?.findStreet()?.asString() ?? null,
      town: patient?.address?.findTown()?.getName() ?? null,
      nfzDepartment: patient?.nfzDepartment ?? null,
      personalIdentifierType: selectPatientSelectIdentifierType(patient),
      birthDate: patient?.birthDate ? patient.birthDate : null,
      gender: genderSelectItems.find((gender) => patient?.gender === gender.value) ?? null,
      ...(patientExternalIdentifier?.accept<Partial<PersonalDataFormBean>>({
         visitPesel: (pesel) => ({
            pesel: pesel,
         }),
         visitGuardianPesel: (guardianPesel) => ({
            guardianPesel: guardianPesel,
         }),
         visitPassport: (passport) => ({
            passport: passport.getValue(),
            passportCountry: passport.getCountry(),
            passportValidTo: passport.getValidTo(),
         }),
         visitNationalIdentificationNumber: (nationalIdentificationNumber) => ({
            nationalIdentificationNumber: nationalIdentificationNumber.getValue(),
            nationalIdentificationNumberCountry: nationalIdentificationNumber.getCountry(),
         }),
      }) ?? null),
      ...(medicalRecordNumberIdentifier
         ? {
              medicalRecordNumber: medicalRecordNumberIdentifier.asString(),
           }
         : null),
      identityDocument: createIdentityDocumentFormPart(patient?.identityDocument),
   };
};

export const countrySelectFilterFn = (searchedValue: string, country: Country): boolean =>
   country.getName(getCurrentLocale()).toLowerCase().includes(searchedValue.toLowerCase());

export const prefixFieldName = (name: string, prefix?: string): string => {
   if (prefix) {
      const nameWithCapitalizedFirstLetter = name[0].toUpperCase() + name.slice(1);
      return prefix + nameWithCapitalizedFirstLetter;
   }
   return name;
};

export const createCountryFilter = (searchText: string | null) => (country: Country): boolean =>
   searchText ? country.getName(getCurrentLocale()).toLowerCase().includes(searchText.toLowerCase()) : true;

export const countryIsoCodeGetter = (country: Country): IsoCode => country.getIsoCode();

export const createNationalIdentificationNumberCountryFilter = (searchText: string | null) => (
   country: NationalIdentificationNumberCountry
): boolean =>
   searchText ? country.getValue().getName(getCurrentLocale()).toLowerCase().includes(searchText.toLowerCase()) : true;

export const nationalIdentificationNumberCountryIsoCodeGetter = (
   country: NationalIdentificationNumberCountry
): IsoCode => country.getValue().getIsoCode();

export const createNfzDepartmentFilter = (searchText: string | null) => (nfzDepartment: NfzDepartment): boolean =>
   searchText
      ? nfzDepartment.name.toLowerCase().includes(searchText.toLowerCase()) ||
        nfzDepartment.code.toString().includes(searchText.toLowerCase())
      : true;

export const getPatientDisplayedFullName = (
   firstName: FirstName | undefined,
   lastName: LastName | undefined
): string | undefined => {
   if (!firstName && !lastName) {
      return undefined;
   }
   if (firstName && lastName) {
      return `${firstName.asString()} ${lastName.asString()}`;
   }
   return (firstName || lastName)?.asString();
};
